import { isSameDay, startOfDay } from 'date-fns';

import { EventSource, EventView, NoteView } from '@ndvr/customer-api';

import { convertBackendTimeToBrowser, formatTime } from '../../utils';

// One card in the Meeting Minutes reading list. An event can carry several notes, which is rare
// enough that the cards do not grow multi-note affordances for it: the list renders one row per
// note instead. Pairing a note with its event makes that explicit — rows outnumber meetings, and
// several rows can share one event — instead of smuggling the pairing through an EventView whose
// `notes` has been trimmed to one.
export type MeetingMinutesRow = { event: EventView; note?: NoteView };

export type DayGroup = { day: Date; rows: MeetingMinutesRow[] };

// The Meeting Minutes cards render every action as a bare underlined link — Add note, Open
// Note, Share — so the treatment lives here, with the tab's other shared helpers, rather than
// being restated in each widget.
export const MEETING_MINUTES_LINK_CLASSES = 'text-cocoa-1000 cursor-pointer text-xs underline';

// The heading above each block inside an expanded card — Summary, Decisions, Next steps,
// Suggestions. Shared for the same reason as the link treatment above: the blocks live in
// different widgets but have to read as one card.
export const MEETING_MINUTES_SECTION_LABEL_CLASSES = 'text-muted-foreground mb-1.5 text-xs tracking-wide uppercase';

// Fans an event out into its rows: one per note, or a single note-less row. `notes` is typed
// non-optional in the generated EventView but arrives null/undefined at runtime for events
// without notes.
export const toMeetingMinutesRows = (event: EventView): MeetingMinutesRow[] =>
    event.notes?.length ? event.notes.map((note) => ({ event, note })) : [{ event }];

// "10:00 AM – 10:45 AM" in the browser's zone, as the minutes rows and the note page header read it.
export const formatEventTimeRange = (
    event: Pick<EventView, 'startTime' | 'startTimeZone' | 'endTime' | 'endTimeZone'>,
): string =>
    `${formatTime(convertBackendTimeToBrowser(event.startTime, event.startTimeZone))} – ${formatTime(
        convertBackendTimeToBrowser(event.endTime, event.endTimeZone),
    )}`;

// Splits AI-generated prose into trimmed non-blank paragraphs. The model emits both single- and
// double-newline breaks, so any newline run is a paragraph boundary.
export const splitIntoParagraphs = (text?: string | null): string[] =>
    text
        ?.split(/\r?\n+/)
        .map((line) => line.trim())
        .filter(Boolean) ?? [];

// Null-safe cleanup for the AI-generated decisions/nextSteps lists: trims entries and drops
// blanks, so "absent" and "empty after cleaning" look the same to callers.
export const cleanNoteList = (items?: (string | null)[] | null): string[] =>
    items?.map((item) => item?.trim() ?? '').filter(Boolean) ?? [];

// Whether the note has any AI section worth a Summary tab, using the same cleanup the sections
// render with, so a tab never opens onto nothing.
export const hasNoteSections = (note: NoteView): boolean =>
    splitIntoParagraphs(note.summary).length > 0 ||
    cleanNoteList(note.decisions).length > 0 ||
    cleanNoteList(note.nextSteps).length > 0;

// The share endpoint only accepts a householdless note that hangs off a LOCAL event with no
// client household, so the Share action mirrors those preconditions here rather than letting the
// card offer an action the backend would reject (AdHocMeetingNoteSharingService).
export const isShareableNote = (event: EventView, note: NoteView): boolean =>
    event.source === EventSource.LOCAL && !event.clientHouseholdGUID && !note.householdGUID;

// Groups are built on browser-local days so the gutter date always matches the times
// rendered on the cards, which are converted with the same util. Days without rows
// produce no group, so the reading list skips them by construction. Inside a day,
// rows with a note come first; both partitions stay ordered by start time (the
// partitioning sort is stable, so the chronological pre-sort survives, which also keeps
// the rows of one multi-note meeting adjacent and in note order).
export const groupRowsByLocalDay = (rows: MeetingMinutesRow[]): DayGroup[] => {
    const sorted = rows
        .map((row) => ({ row, start: convertBackendTimeToBrowser(row.event.startTime, row.event.startTimeZone) }))
        .sort((a, b) => a.start.getTime() - b.start.getTime());

    const groups: DayGroup[] = [];
    for (const { row, start } of sorted) {
        const lastGroup = groups[groups.length - 1];
        if (lastGroup && isSameDay(lastGroup.day, start)) {
            lastGroup.rows.push(row);
        } else {
            groups.push({ day: startOfDay(start), rows: [row] });
        }
    }

    groups.forEach((group) => group.rows.sort((a, b) => Number(Boolean(b.note)) - Number(Boolean(a.note))));
    return groups;
};
